import React from "react";
import { Check, OctagonAlertIcon, AlertTriangle } from "lucide-react";
import { Button } from "component/ui/Button";
import { cn } from "lib/utils";
import { Danger, Warning2 } from "iconsax-reactjs";

/**
 * AccountOpeningBanner
 * Top notification banner for the account opening fee flow.
 *
 * Variants / Statuses:
 * - "approved" / "APPROVED" / "payment_required":
 *     Green banner alerting the user that their business account has been approved and to pay opening fee.
 * - "pending" / "PENDING" / "checking":
 *     Amber banner alerting the user that the opening payment is being verified.
 * - "failed" / "FAILED" / "error":
 *     Red banner alerting the user that payment could not be completed, with retry option.
 */
const AccountOpeningBanner = ({
  status = "approved",
  feeAmount = "₦20,000",
  onPay,
  onRetry,
  onLearnMore,
  onNeedHelp,
  className = "",
  overrideTitle,
  overrideMessage,
}) => {
  const normalizedStatus = String(status || "approved").toLowerCase();

  const isApproved =
    normalizedStatus === "approved" ||
    normalizedStatus === "payment_required" ||
    normalizedStatus === "ready";

  const isPending =
    normalizedStatus === "pending" ||
    normalizedStatus === "checking" ||
    normalizedStatus === "processing";

  const isFailed =
    normalizedStatus === "failed" ||
    normalizedStatus === "error" ||
    normalizedStatus === "rejected";

  // 1. APPROVED / READY TO PAY (Green)
  if (isApproved) {
    return (
      <div
        className={cn(
          "w-full bg-success-bg border-t-0 border-x-0 border-solid border-b-[0.5px] border-success-border h-[62px] px-4 md:px-8 py-2.5 sm:py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 transition-all",
          className
        )}
      >
        <div className="flex items-center gap-x-3  pl-3">
          <div className="shrink-0 size-5 rounded-full bg-success-icon flex items-center justify-center text-white shadow-xs">
            <Check className="size-3.5 stroke-[3]" />
          </div>

          <div className="flex flex-wrap items-center gap-x-2 text-sm leading-relaxed">
            <span className="font-medium text-success-text">
              {overrideTitle || "Your business account has been approved"}
            </span>
            <span className="text-slate-text font-[350]">
              {overrideMessage || (
                <>
                  Pay the one-time account opening fee of{" "}
                  <span className="font-medium text-black">
                    {feeAmount ? `${feeAmount}` : "₦0"}
                  </span>{" "}
                  to complete your account setup
                </>
              )}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0 self-end sm:self-auto">
          {onLearnMore && (
            <Button
              type="button"
              variant="ghost"
              onClick={onLearnMore}
              className="text-sm font-medium text-main-dark hover:text-main px-2 h-auto py-1 shadow-none bg-transparent hover:bg-transparent cursor-pointer"
            >
              Learn more
            </Button>
          )}

          <Button
            type="button"
            onClick={onPay}
            className="!bg-btn-dark hover:!bg-btn-dark/80 text-white text-sm font-medium px-4 h-9 rounded-lg transition-colors cursor-pointer shadow-sm shrink-0"
          >
            Complete setup
          </Button>
        </div>
      </div>
    );
  }

  // 2. PENDING / CHECKING PAYMENT (Amber / Cream)
  if (isPending) {
    return (
      <div
        className={cn(
          "w-full bg-warning-bg border-t-0 border-x-0  border-solid border-b-[0.5px] border-warning-border h-[62px] px-4 md:px-8 py-2.5 sm:py-3 flex flex-col sm:flex-row items-start sm:items-center justify-center gap-3 transition-all",
          className
        )}
      >
        <div className="flex items-center gap-x-3  pl-3">
          <div className="shrink-0 flex items-center justify-center">
            <Warning2 variant="Bold" color="#93360D" size={18} />
          </div>

          <div className="flex flex-wrap items-center gap-x-2 text-sm leading-relaxed">
            <span className="font-medium text-warning-text">
              {overrideTitle || "We’re checking your account opening payment"}
            </span>
            <span className="text-slate-text font-[350]">
              {overrideMessage || "Your account will be ready once the payment is confirmed."}
            </span>
          </div>
        </div>

        {onNeedHelp && (
          <div className="flex items-center gap-3 shrink-0 self-end sm:self-auto">
            <Button
              type="button"
              variant="ghost"
              onClick={onNeedHelp}
              className="text-sm font-medium text-main-dark hover:text-main px-2 h-auto py-1 shadow-none bg-transparent hover:bg-transparent cursor-pointer"
            >
              Need help?
            </Button>
          </div>
        )}
      </div>
    );
  }

  // 3. FAILED (Red / Coral)
  if (isFailed) {
    return (
      <div
        className={cn(
          "w-full bg-error-bg border-t-0 border-x-0 border-solid border-b-[0.5px] border-error-border h-[62px] px-4 md:px-8 py-2.5 sm:py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 transition-all",
          className
        )}
      >
        <div className="flex items-center gap-x-3 pl-3">
          <div className="shrink-0 flex items-center justify-center">
            <Danger variant="Bold" color="#F73C3C" size={18} />
          </div>

          <div className="flex flex-wrap items-center gap-x-2 text-sm leading-relaxed">
            <span className="font-medium text-error-text">
              {overrideTitle || "We couldn’t complete your account opening payment"}
            </span>
            <span className="text-slate-text font-[350]">
              {overrideMessage ||
                "Try again to fund your wallet and finish setting up your account."}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0 self-end sm:self-auto">
            <Button
              type="button"
              variant="ghost"
              onClick={onNeedHelp}
              className="text-sm font-medium text-main-dark hover:text-main px-2 h-auto py-1 shadow-none bg-transparent hover:bg-transparent cursor-pointer"
            >
              Need help?
            </Button>

          <Button
            type="button"
            onClick={onRetry || onPay}
            className="!bg-btn-dark hover:!bg-btn-dark/80 text-white text-sm font-medium px-4 h-9 rounded-lg transition-colors cursor-pointer shadow-sm shrink-0"
          >
            Retry payment
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default AccountOpeningBanner;
